import React from 'react';
import { useTranslation } from 'react-i18next';
import { Notice, Button, Dropdown, Chip, IconButton } from '@origo/origo-ui';
import { FunnelIcon, ArrowsDownUpIcon, XIcon } from '@phosphor-icons/react';
import PlanSearchBar, { type PlanSearchBarProps } from './components/PlanSearchBar';
import PlanFilterPanel, { type PlanFilterPanelProps } from '../../filter/PlanFilterPanel';
import PlanResultCard from './components/PlanResultCard';
import { sortOptions, type SortOption } from '../../filter/types';
import type { PlanSummary } from '../../types';
import styles from './PlanLandingPage.module.css';

export interface ActiveFilterChip {
    id: string;
    label: string;
    onRemove: () => void;
}

interface PlanLandingPageProps {
    onCreatePlan: () => void;
    search: PlanSearchBarProps;
    /** Props til filterpanelet i sidepanelet (showSortControl=false). */
    filter: PlanFilterPanelProps;
    filterOpen: boolean;
    onToggleFilter: () => void;
    activeFilterCount: number;
    activeFilterChips: ActiveFilterChip[];
    onClearAllFilters: () => void;
    sortOption: SortOption;
    onSortChange: (o: SortOption) => void;
    /** true når søk/filter er aktivt → vis treff; ellers vis «Nylig jobbet med». */
    isSearching: boolean;
    recentItems: PlanSummary[];
    resultItems: PlanSummary[];
    totalCount: number;
    onZoomTo: (napId: string) => void;
    onSelect: (napId: string) => void;
}

const PlanLandingPage: React.FC<PlanLandingPageProps> = ({
    onCreatePlan,
    search,
    filter,
    filterOpen,
    onToggleFilter,
    activeFilterCount,
    activeFilterChips,
    onClearAllFilters,
    sortOption,
    onSortChange,
    isSearching,
    recentItems,
    resultItems,
    totalCount,
    onZoomTo,
    onSelect,
}) => {
    const { t } = useTranslation();

    const renderList = (items: PlanSummary[], compact = false) => (
        <ul className={`${styles.results} ${compact ? styles.resultsCompact : ''}`}>
            {items.map(plan => (
                <li key={plan.nasjonalArealplanId} className={styles.resultItem}>
                    <PlanResultCard plan={plan} onZoomTo={onZoomTo} onSelect={onSelect} />
                </li>
            ))}
        </ul>
    );

    return (
        <div className={styles.root}>
            <div className={styles.mainColumn}>
                <header className={styles.header}>
                    <div className={styles.headerText}>
                        <h1 className={styles.title}>{t('landing.title')}</h1>
                        <p className={styles.subtitle}>{t('landing.subtitle')}</p>
                    </div>
                    <Button variant="primary" onClick={onCreatePlan}>
                        {t('landing.createPlan')}
                    </Button>
                </header>

                <div className={styles.stickyControls}>
                    <div className={styles.searchRow}>
                        <div className={styles.searchField}>
                            <PlanSearchBar {...search} />
                        </div>
                        <Button
                            variant="secondary"
                            className={styles.filterButton}
                            onClick={onToggleFilter}
                            aria-expanded={filterOpen}
                            aria-controls="plan-filter-column"
                        >
                            <FunnelIcon size={16} aria-hidden />
                            {t('landing.filterToggle')}
                            {activeFilterCount > 0 && (
                                <span
                                    className={styles.filterCountBadge}
                                    aria-label={t('landing.activeFilters', { count: activeFilterCount })}
                                >
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>
                        <Dropdown
                            align="end"
                            className={styles.sortDropdown}
                            trigger={
                                <span className={styles.sortTrigger} aria-label={t('landing.sortLabel')}>
                                    <ArrowsDownUpIcon size={18} aria-hidden />
                                </span>
                            }
                            groups={[{
                                items: sortOptions.map(o => ({
                                    id: `${o.field}-${o.direction}`,
                                    label: t(o.labelKey),
                                    selected: o.field === sortOption.field && o.direction === sortOption.direction,
                                    onClick: () => onSortChange(o),
                                })),
                            }]}
                        />
                    </div>

                    {isSearching && (
                        <div className={styles.resultMeta}>
                            <span className={styles.resultCount}>
                                {t('landing.resultCount', { count: resultItems.length, total: totalCount })}
                            </span>
                            {activeFilterChips.length > 0 && (
                                <>
                                    <div className={styles.filterChips}>
                                        {activeFilterChips.map(c => (
                                            <Chip key={c.id} onRemove={c.onRemove} removeLabel={`${t('common.close')}: ${c.label}`}>
                                                {c.label}
                                            </Chip>
                                        ))}
                                    </div>
                                    {activeFilterChips.length > 1 && (
                                        <button type="button" className={styles.clearAll} onClick={onClearAllFilters}>
                                            {t('landing.clearAll')}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {isSearching ? (
                    resultItems.length === 0 ? (
                        <Notice variant="info" className={styles.empty}>
                            {t('landing.searchResults.empty', { term: search.value })}
                        </Notice>
                    ) : (
                        renderList(resultItems)
                    )
                ) : (
                    <>
                        {/* «Nylig» skjules når den er tom — hele lista ligger uansett under (F7.1.1, #14552). */}
                        {recentItems.length > 0 && (
                            <section className={styles.recentSection} aria-label={t('landing.recent.title')}>
                                <div className={styles.recentHeader}>
                                    <div className={styles.headerText}>
                                        <h2 className={styles.recentTitle}>{t('landing.recent.title')}</h2>
                                        <p className={styles.subtitle}>{t('landing.recent.subtitle')}</p>
                                    </div>
                                </div>
                                {renderList(recentItems, true)}
                            </section>
                        )}
                        {/* Standardlista: alle planer i tjenesten (tjenesten er per kommune), kontinuerlig
                            scroll uten paginering — bevisst avvik fra spec F7.1.4, se Feature 14544. */}
                        <section className={styles.recentSection} aria-label={t('landing.all.title', { count: totalCount })}>
                            <div className={styles.recentHeader}>
                                <div className={styles.headerText}>
                                    <h2 className={styles.recentTitle}>{t('landing.all.title', { count: totalCount })}</h2>
                                    <p className={styles.subtitle}>{t('landing.all.subtitle')}</p>
                                </div>
                            </div>
                            {resultItems.length === 0 ? (
                                <Notice variant="info" className={styles.empty}>
                                    {t('landing.all.empty')}
                                </Notice>
                            ) : (
                                renderList(resultItems)
                            )}
                        </section>
                    </>
                )}
            </div>

            {filterOpen && (
                <aside id="plan-filter-column" className={styles.filterColumn} aria-label={t('landing.filterToggle')}>
                    <div className={styles.filterHeader}>
                        <h2 className={styles.filterTitle}>{t('landing.filterToggle')}</h2>
                        <IconButton
                            icon={<XIcon size={14} aria-hidden />}
                            label={t('landing.filterClose')}
                            variant="ghost"
                            size="sm"
                            onClick={onToggleFilter}
                        />
                    </div>
                    <div className={styles.filterContent}>
                        <PlanFilterPanel {...filter} />
                    </div>
                </aside>
            )}
        </div>
    );
};

export default PlanLandingPage;
